import { useMemo } from 'react';
import { parseISO, differenceInMonths, startOfMonth, subMonths } from 'date-fns';
import { PRO_FORMA_TABS } from 'views/ProFormaTable/constants';
import { useProFormaSocketContext } from '../socketContext/ProFormaSocketProvider';

export interface Milestone {
    label: string;
    date: string;
    monthNumber: number | null;
    tabName: string;
}

interface MilestoneConfig {
    label: string;
    date: string | null | undefined;
    monthNumber?: number | null;
    tabName: string;
    enabled?: boolean;
}

const useMilestones = (): Milestone[] => {
    const { proForma, uses, operatingInfo } = useProFormaSocketContext();

    return useMemo(() => {
        const analysisStart = proForma.startDate
            ? subMonths(startOfMonth(parseISO(proForma.startDate)), 1)
            : null;

        const computeMonthNumber = (dateStr: string): number | null => {
            if (!analysisStart) return null;
            try {
                return differenceInMonths(startOfMonth(parseISO(dateStr)), analysisStart);
            } catch {
                return null;
            }
        };

        // Insertion order defines tiebreaker when month numbers are equal (lifecycle sequence)
        const configs: MilestoneConfig[] = [
            {
                label: 'Start month', date: proForma.startDate,
                monthNumber: 1, tabName: PRO_FORMA_TABS.GENERAL_INFORMATION.name,
            },
            {
                label: 'Development start', date: uses.developmentStartDate,
                monthNumber: uses.developmentStartMonth, tabName: PRO_FORMA_TABS.DEVELOPMENT.name,
            },
            {
                label: 'Construction start', date: proForma.constructionStart,
                tabName: PRO_FORMA_TABS.GENERAL_INFORMATION.name,
            },
            {
                label: 'Development end', date: uses.developmentEndDate,
                monthNumber: uses.developmentEndMonth, tabName: PRO_FORMA_TABS.DEVELOPMENT.name,
            },
            {
                label: 'Operation start', date: operatingInfo.operationStartDate,
                monthNumber: operatingInfo.operationBeginMonth, tabName: PRO_FORMA_TABS.OPERATIONS.name,
            },
            {
                label: '1st stabilized month', date: operatingInfo.stabilizedDate,
                monthNumber: operatingInfo.stabilizedMonth, tabName: PRO_FORMA_TABS.OPERATIONS.name,
            },
            {
                label: 'Loan refinance', date: operatingInfo.loanFundingDate,
                monthNumber: operatingInfo.loanFundingMonth, tabName: PRO_FORMA_TABS.OPERATIONS.name,
                enabled: !!operatingInfo.includePermanentDebt,
            },
            {
                label: 'Sale month', date: proForma.saleDate,
                monthNumber: proForma.saleMonth, tabName: PRO_FORMA_TABS.GENERAL_INFORMATION.name,
            },
        ];

        return configs
            .filter((c): c is MilestoneConfig & { date: string } => (
                !!c.date && (c.enabled ?? true)
            ))
            .map((c) => ({
                label: c.label,
                date: c.date,
                monthNumber: c.monthNumber ?? computeMonthNumber(c.date),
                tabName: c.tabName,
            }))
            .sort((a, b) => (a.monthNumber ?? 0) - (b.monthNumber ?? 0));
    }, [proForma, uses, operatingInfo]);
};

export default useMilestones;
